import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Sparkles, X, Loader2, Trash2, Save, Wand2 } from "lucide-react";

const MONTHS_DE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/**
 * Modal: Enter plant names (one per line) → Claude generates structured plant data
 * → admin can edit each card → bulk save.
 */
export default function AIPlantGenerator({ onDone }) {
  const [open, setOpen] = useState(false);
  const [names, setNames] = useState("");
  const [hint, setHint] = useState("auto");
  const [generating, setGenerating] = useState(false);
  const [plants, setPlants] = useState([]); // generated previews
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState("");

  const reset = () => {
    setNames(""); setPlants([]); setErrors([]); setProgress("");
  };

  const generate = async () => {
    const list = names.split("\n").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return toast.error("Bitte mindestens einen Pflanzennamen eingeben");
    if (list.length > 20) return toast.error("Max. 20 Pflanzen pro Durchgang");
    setGenerating(true);
    setErrors([]);
    setProgress(`Starte Job für ${list.length} Pflanze${list.length === 1 ? "" : "n"}…`);
    try {
      // 1) Start background job
      const start = await api.post("/admin/plants/ai-generate-async", { names: list, category_hint: hint });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Kein job_id erhalten");
      // 2) Poll every 3s (max 10 min)
      const startedAt = Date.now();
      const maxMs = 10 * 60 * 1000;
      let finalPlants = [];
      let finalErrors = [];
      while (Date.now() - startedAt < maxMs) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const s = await api.get(`/admin/jobs/${jobId}`);
        const st = s.data || {};
        setProgress(st.message || `KI läuft … ${st.progress || 0}%`);
        if (st.status === "done") {
          finalPlants = st.result?.plants || [];
          finalErrors = st.result?.errors || [];
          break;
        }
        if (st.status === "error") {
          throw new Error(st.error || "Job-Fehler");
        }
      }
      setPlants(finalPlants);
      setErrors(finalErrors);
      setProgress(`${finalPlants.length} Pflanze${finalPlants.length === 1 ? "" : "n"} generiert`);
      toast.success(`KI fertig: ${finalPlants.length} Pflanzen`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const updateField = (i, field, val) => {
    const arr = [...plants];
    arr[i] = { ...arr[i], [field]: val };
    setPlants(arr);
  };

  const toggleMonth = (i, m) => {
    const arr = [...plants];
    const cur = arr[i].season || [];
    arr[i] = { ...arr[i], season: cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m].sort((a, b) => a - b) };
    setPlants(arr);
  };

  const removeOne = (i) => {
    setPlants(plants.filter((_, idx) => idx !== i));
  };

  const saveAll = async () => {
    if (plants.length === 0) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/plants/bulk-insert", { plants });
      const total = (r.data.created || 0) + (r.data.updated || 0);
      toast.success(`${total} Pflanze${total === 1 ? "" : "n"} gespeichert (${r.data.created} neu, ${r.data.updated} aktualisiert)`);
      if (r.data.errors && r.data.errors.length) {
        toast.error(`${r.data.errors.length} Fehler – siehe Konsole`);
        console.warn("Bulk insert errors:", r.data.errors);
      }
      reset();
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary !py-2 !px-4" title="Pflanzen mit KI generieren" data-testid="ai-gen-open">
        <Sparkles className="w-4 h-4" /> KI-Generator
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => !generating && !saving && setOpen(false)} data-testid="ai-gen-modal">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 px-6 py-4 flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
          <div className="flex items-center gap-3">
            <Wand2 className="w-5 h-5" style={{ color: "var(--wp-primary)" }} />
            <h3 className="font-display text-xl">KI-Pflanzen-Generator</h3>
            <span className="text-xs opacity-60">Claude Sonnet 4.5</span>
          </div>
          <button onClick={() => !generating && !saving && setOpen(false)} className="opacity-70 hover:opacity-100" disabled={generating || saving}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Input */}
          <div>
            <div className="overline mb-2">Pflanzennamen (eine pro Zeile, max. 20)</div>
            <textarea
              value={names}
              onChange={(e) => setNames(e.target.value)}
              placeholder="Beifuß&#10;Schöllkraut&#10;Wiesensalbei&#10;Wilde Möhre"
              className="w-full px-4 py-3 rounded-xl border min-h-[140px] font-mono text-sm"
              style={{ borderColor: "var(--wp-border)" }}
              disabled={generating}
              data-testid="ai-gen-names"
            />
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <select value={hint} onChange={(e) => setHint(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} disabled={generating} data-testid="ai-gen-hint">
                <option value="auto">Kategorie: Auto</option>
                <option value="edible">Essbar</option>
                <option value="medicinal">Heilkraut</option>
                <option value="toxic">Giftig</option>
              </select>
              <button onClick={generate} disabled={generating || saving} className="btn-primary" data-testid="ai-gen-generate">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? "Generiere…" : "Generieren"}
              </button>
              {progress && <span className="text-xs opacity-70">{progress}</span>}
            </div>
            <div className="text-[11px] opacity-60 mt-2">💡 Dauer: ~10–15 Sek. pro Pflanze (4 parallel). Bilder musst du danach noch zuweisen.</div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="rounded-xl p-3 text-xs" style={{ backgroundColor: "rgba(167,58,58,0.08)" }}>
              <b>KI-Fehler:</b>
              <ul className="mt-1 space-y-1">{errors.map((e, i) => <li key={i}>• <b>{e.name}:</b> {e.error}</li>)}</ul>
            </div>
          )}

          {/* Generated cards */}
          {plants.length > 0 && (
            <div>
              <div className="overline mb-3">Vorschau · {plants.length} Pflanze{plants.length === 1 ? "" : "n"} – bearbeitbar</div>
              <div className="grid lg:grid-cols-2 gap-4">
                {plants.map((p, i) => (
                  <div key={i} className="plant-card p-4 space-y-2" data-testid={`ai-card-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <input value={p.name_de} onChange={(e) => updateField(i, "name_de", e.target.value)} className="flex-1 font-display text-lg px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} data-testid={`ai-name-${i}`} />
                      <button onClick={() => removeOne(i)} className="text-[var(--wp-toxic)] p-1" title="Verwerfen" data-testid={`ai-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <input value={p.name_lat} onChange={(e) => updateField(i, "name_lat", e.target.value)} placeholder="Lateinisch" className="w-full italic text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={p.slug} onChange={(e) => updateField(i, "slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="slug" className="text-xs font-mono px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                      <select value={p.category} onChange={(e) => updateField(i, "category", e.target.value)} className="text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }}>
                        <option value="edible">essbar</option>
                        <option value="medicinal">heilkraut</option>
                        <option value="toxic">giftig</option>
                      </select>
                    </div>
                    <textarea value={p.short_description} onChange={(e) => updateField(i, "short_description", e.target.value)} placeholder="Kurzbeschreibung" className="w-full text-xs px-2 py-1 rounded border min-h-[44px]" style={{ borderColor: "var(--wp-border)" }} />
                    <textarea value={p.description} onChange={(e) => updateField(i, "description", e.target.value)} placeholder="Beschreibung" className="w-full text-xs px-2 py-1 rounded border min-h-[64px]" style={{ borderColor: "var(--wp-border)" }} />
                    <input value={p.ingredients} onChange={(e) => updateField(i, "ingredients", e.target.value)} placeholder="Inhaltsstoffe" className="w-full text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                    <input value={p.habitat} onChange={(e) => updateField(i, "habitat", e.target.value)} placeholder="Standort" className="w-full text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                    <input value={p.look_alikes} onChange={(e) => updateField(i, "look_alikes", e.target.value)} placeholder="Verwechslungen" className="w-full text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                    <input value={p.uses} onChange={(e) => updateField(i, "uses", e.target.value)} placeholder="Verwendung" className="w-full text-xs px-2 py-1 rounded border" style={{ borderColor: "var(--wp-border)" }} />
                    {p.warnings && (
                      <textarea value={p.warnings} onChange={(e) => updateField(i, "warnings", e.target.value)} placeholder="Warnungen" className="w-full text-xs px-2 py-1 rounded border min-h-[40px]" style={{ borderColor: "rgba(167,58,58,0.4)", backgroundColor: "rgba(167,58,58,0.05)" }} />
                    )}
                    <div>
                      <div className="overline text-[9px] mb-1">Saison</div>
                      <div className="flex flex-wrap gap-1">
                        {MONTHS_DE.map((m, idx) => {
                          const month = idx + 1;
                          const active = (p.season || []).includes(month);
                          return (
                            <button key={month} onClick={() => toggleMonth(i, month)} className={`text-[10px] px-1.5 py-1 rounded ${active ? "bg-[var(--wp-primary)] text-[var(--wp-cream)]" : "bg-[var(--wp-cream)] border"}`} style={!active ? { borderColor: "var(--wp-border)" } : {}}>
                              {m}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {plants.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-between" style={{ borderColor: "var(--wp-border)" }}>
            <div className="text-sm opacity-70">{plants.length} Pflanze{plants.length === 1 ? "" : "n"} zum Speichern – Slugs werden geprüft (Upsert)</div>
            <div className="flex gap-2">
              <button onClick={reset} className="btn-secondary" disabled={saving}>Verwerfen</button>
              <button onClick={saveAll} disabled={saving} className="btn-primary" data-testid="ai-save-all">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Speichere…" : `Alle ${plants.length} speichern`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
